/**
 * FreshserviceAttachmentsCard — surfaces files attached to the source
 * Freshservice ticket (kickstart briefs, supporting docs) on a promoted
 * project's page.
 *
 * This closes the ETE-153 gap: a user attaches a kickstart brief to the
 * FS ticket AFTER promotion, and it never reached the planner. Now the
 * project mirrors the ticket's attachment metadata and re-fetches a
 * fresh signed download link per file on click (the signed URLs FS
 * issues are short-lived, so we never store them).
 *
 * Only mounted for source === "freshservice" projects. The "Refresh"
 * button forces a re-pull so a just-attached file shows up on demand
 * rather than waiting for the cache to expire.
 */
import { Loader2, Paperclip, RefreshCw, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import { apiFileHref } from "@/lib/api";
import {
  useFreshserviceProjectAttachments,
  useRefreshFreshserviceProjectAttachments,
  type ProjectFreshserviceAttachment,
} from "@/hooks/useFreshservice";

function formatBytes(bytes: number | null): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function relativeSync(iso: string | null): string {
  if (!iso) return "";
  const t = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z").getTime();
  if (Number.isNaN(t)) return "";
  const mins = Math.max(0, Math.floor((Date.now() - t) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function FreshserviceAttachmentsCard({ projectId }: { projectId: string }) {
  const q = useFreshserviceProjectAttachments(projectId);
  const refresh = useRefreshFreshserviceProjectAttachments(projectId);

  const handleRefresh = async () => {
    try {
      await refresh.mutateAsync();
      toast.success("Refreshed from Freshservice");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? "Couldn't refresh from Freshservice");
    }
  };

  const data = q.data;
  const attachments = data?.attachments ?? [];

  return (
    <div className="rounded-xl bg-surface p-5 ring-1 ring-line">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-brand-navy-500" />
          <div>
            <h2 className="text-sm font-semibold text-ink">Files from Freshservice</h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              Attachments on the source ticket — kickstart briefs, supporting docs.
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refresh.isPending}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs text-ink-3 hover:bg-raised disabled:opacity-50"
          title="Re-pull attachments from Freshservice"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", refresh.isPending && "animate-spin")} />
          Refresh
        </button>
      </div>

      <div className="mt-4">
        {q.isLoading ? (
          <div className="flex justify-center py-6 text-ink-subtle">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : data?.sync_error ? (
          <div className="rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-300">
            Couldn't reach Freshservice to load files. {data.sync_error}{" "}
            <button onClick={handleRefresh} className="font-semibold underline underline-offset-2">
              Try again
            </button>
          </div>
        ) : attachments.length === 0 ? (
          <div className="rounded-lg border border-dashed border-line bg-canvas px-4 py-6 text-center">
            <p className="text-sm text-ink-3">No files on the Freshservice ticket yet.</p>
            <p className="mt-1 text-xs text-ink-muted">
              Attach a kickstart brief on the ticket, then hit <strong>Refresh</strong>.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {attachments.map((a) => (
              <AttachmentRow key={a.id} attachment={a} />
            ))}
          </div>
        )}
      </div>

      {data?.last_synced_at && (
        <div className="mt-3 text-right text-[11px] text-ink-subtle">
          Synced {relativeSync(data.last_synced_at)}
        </div>
      )}
    </div>
  );
}

function AttachmentRow({ attachment: a }: { attachment: ProjectFreshserviceAttachment }) {
  const size = formatBytes(a.size);
  return (
    <a
      href={apiFileHref(a.open_url)}
      target="_blank"
      rel="noreferrer"
      className="group flex items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2 transition-colors hover:border-brand-navy-300 hover:bg-brand-navy-50/40 dark:hover:bg-brand-navy-500/10"
      title={`Open ${a.name} (fetches a fresh download link from Freshservice)`}
    >
      <FileText className="h-4 w-4 shrink-0 text-brand-navy-500" />
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink group-hover:text-brand-navy-700">
        {a.name}
      </span>
      {size && <span className="shrink-0 text-[11px] tabular-nums text-ink-subtle">{size}</span>}
      <ExternalLink className="h-3 w-3 shrink-0 text-ink-faint group-hover:text-brand-navy-500" />
    </a>
  );
}
